import { Component, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { MembershipService } from 'src/app/services/membership.service';
import { versions } from 'src/app/versions';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Pulls the project uuid out of an app link like /main/project/<uuid>/errors/<issue>. */
export function projectUuidFromPath(path: string | null | undefined): string | null {
  const match = /\/main\/(?:project|status)\/([0-9a-f-]{36})(?:[/?#]|$)/i.exec(path ?? '');
  return match && UUID.test(match[1]) ? match[1] : null;
}

type Field = 'name' | 'email' | 'message';

@Component({
  selector: 'app-request-access',
  templateUrl: './request-access.component.html',
  styleUrls: ['./request-access.component.scss'],
  standalone: false,
})
export class RequestAccessComponent implements OnInit {
  readonly versions = versions;
  readonly submitting = signal(false);
  readonly sent = signal(false);
  readonly formError = signal<string | null>(null);

  projectUuid: string | null = null;
  path = '';

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    message: new FormControl(''),
  });

  constructor(
    private route: ActivatedRoute,
    private membership: MembershipService,
  ) {}

  ngOnInit(): void {
    this.path = this.route.snapshot.queryParamMap.get('next') ?? '';
    this.projectUuid = projectUuidFromPath(this.path);
  }

  invalid(name: Field): boolean {
    const control = this.form.get(name);
    return !!control && control.touched && control.invalid;
  }

  has(name: Field, error: string): boolean {
    const control = this.form.get(name);
    return !!control && control.touched && control.hasError(error);
  }

  async submit() {
    this.form.markAllAsTouched();
    this.formError.set(null);
    if (this.form.invalid || this.submitting() || !this.projectUuid) return;

    this.submitting.set(true);
    const value = this.form.getRawValue();
    try {
      await lastValueFrom(
        this.membership.requestAccess({
          project_uuid: this.projectUuid,
          email: value.email ?? '',
          name: value.name ?? '',
          message: value.message ?? '',
          path: this.path.slice(0, 500),
        }),
      );
      this.sent.set(true);
    } catch (err: any) {
      this.formError.set(err?.detail ?? 'Could not send your request. Please try again later.');
    } finally {
      this.submitting.set(false);
    }
  }
}
